import {
  FlowType,
  GUEST_FLOW_RV,
  GUEST_FLOW_TENT,
  HOST_BOONDOCK_FLOW,
  HOST_MULTISITE_FLOW,
  HOST_SINGLESITE_FLOW,
  ISignUpFlowStep,
  NGRX_BUCKETS,
  SALES_MULTISITE_FLOW,
  SALES_SINGLESITE_FLOW,
} from '@curbnturf/entities';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { SignUpStateState } from './sign-up-state.reducer';

// Lookup the 'SignUpState' feature state managed by NgRx
export const getSignUpStateState = createFeatureSelector<SignUpStateState>(NGRX_BUCKETS.signUpState);

export const getSpinner = createSelector(getSignUpStateState, (state: SignUpStateState) => state.spinner);

export const getGuest = createSelector(getSignUpStateState, (state: SignUpStateState) => state.user?.role === 'guest');

export const getRedirect = createSelector(getSignUpStateState, (state: SignUpStateState) => state.redirect);

export const getUser = createSelector(getSignUpStateState, (state: SignUpStateState) => state.user);

export const getListing = createSelector(getSignUpStateState, (state: SignUpStateState) => state.listing);

export const getListingSites = createSelector(
  getSignUpStateState,
  (state: SignUpStateState) => state.listing?.sites ?? [],
);

export const getSelectedSite = createSelector(getSignUpStateState, (state: SignUpStateState) => state.selectedSite);

export const getBoondock = createSelector(getSignUpStateState, (state: SignUpStateState) => state.boondock);

export const getRv = createSelector(getSignUpStateState, (state: SignUpStateState) => state.user?.selectedRv);

export const isLastStep = createSelector(getSignUpStateState, (state: SignUpStateState) => {
  if (state.currentStep) {
    const foundStepIndex = state.steps.findIndex((el) => el === state.currentStep);
    if (foundStepIndex !== -1) {
      if (state.steps.length - 1 === foundStepIndex) {
        return true;
      }
    }
  }
  return false;
});

export const isFirstStep = createSelector(getSignUpStateState, (state: SignUpStateState) => {
  return state.currentStep ? state.steps.findIndex((el) => el === state.currentStep) === 0 : true;
});

export const previousStep = createSelector(getSignUpStateState, (state: SignUpStateState) => {
  if (state.currentStep) {
    const foundIndex = state.steps.findIndex((el) => el === state.currentStep);
    if (foundIndex >= 1) {
      return state.steps[foundIndex - 1];
    }
  }
  return undefined;
});

export const nextStep = createSelector(getSignUpStateState, (state: SignUpStateState) => {
  if (state.currentStep) {
    const foundIndex = state.steps.findIndex((el) => el === state.currentStep);
    if (foundIndex >= 0 && foundIndex + 1 !== state.steps.length) {
      return state.steps[foundIndex + 1];
    }
  }
  return undefined;
});

export const getCurrentStep = createSelector(getSignUpStateState, (state: SignUpStateState) => state.currentStep);
export const getSteps = createSelector(getSignUpStateState, (state: SignUpStateState) => state.steps);

export const getCompleteSteps = createSelector(
  getSteps,
  getCurrentStep,
  isLastStep,
  isFirstStep,
  previousStep,
  nextStep,
  (
    steps: ISignUpFlowStep[],
    currentStep: ISignUpFlowStep,
    isLastStep: boolean,
    isFirstStep: boolean,
    previousStep?: ISignUpFlowStep,
    nextStep?: ISignUpFlowStep,
  ) => ({
    steps: steps,
    currentStep: currentStep,
    previousStep,
    nextStep,
    isLastStep,
    isFirstStep,
  }),
);

export const getPercentComplete = createSelector(getSignUpStateState, (state: SignUpStateState) => {
  return state.percentComplete || 0;
});

export const getFlowType = createSelector(getSteps, (steps: ISignUpFlowStep[]) => {
  // is Host
  if (
    HOST_SINGLESITE_FLOW.reduce((match, step, index) => {
      // only check if match is true once it is false always return false
      if (match) {
        return step.type === steps[index]?.type;
      }

      return false;
    }, true) ||
    HOST_MULTISITE_FLOW.reduce((match, step, index) => {
      // only check if match is true once it is false always return false
      if (match) {
        return step.type === steps[index]?.type;
      }

      return false;
    }, true)
  ) {
    return FlowType.listing;
  }

  // is Boondock
  if (
    HOST_BOONDOCK_FLOW.reduce((match, step, index) => {
      // only check if match is true once it is false always return false
      if (match) {
        return step.type === steps[index]?.type;
      }

      return false;
    }, true)
  ) {
    return FlowType.boondock;
  }

  // is Guest
  if (
    GUEST_FLOW_RV.reduce((match, step, index) => {
      // only check if match is true once it is false always return false
      if (match) {
        return step.type === steps[index]?.type;
      }

      return false;
    }, true) ||
    GUEST_FLOW_TENT.reduce((match, step, index) => {
      // only check if match is true once it is false always return false
      if (match) {
        return step.type === steps[index]?.type;
      }

      return false;
    }, true)
  ) {
    return FlowType.guest;
  }

  // is Guest
  if (
    SALES_SINGLESITE_FLOW.reduce((match, step, index) => {
      // only check if match is true once it is false always return false
      if (match) {
        return step.type === steps[index]?.type;
      }

      return false;
    }, true) ||
    SALES_MULTISITE_FLOW.reduce((match, step, index) => {
      // only check if match is true once it is false always return false
      if (match) {
        return step.type === steps[index]?.type;
      }

      return false;
    }, true)
  ) {
    return FlowType.sales;
  }

  // default to listing
  return FlowType.listing;
});
